// auditRows.tsx — how one audit line READS: the sentence (who · the verb · the
// object · its name · one inline detail), the leaf under it (a diff, a link's
// edges), the full payload the peek shows, the day header in the person's zone,
// the glyph on the rail. One vocabulary for the page's rows and the peek on
// PeekHost, so the two can never disagree (`auditDisplay` holds the words,
// field labels and value rendering shared with the entities' own trails).

import type { TFunction } from 'i18next';
import type { AuditEvent } from '@ems-app/schemas/audit/responses';
import { addDays, dayIn } from '@ems-app/schemas/common/period';
import { useEntityPeek } from '../../../hooks/useEntityPeek.js';
import { formatDate } from '../../../lib/format.js';
import { EntityRef } from '../../../components/patterns/EntityRef.js';
import { icons } from '../../../components/ui/icons.js';
import { actionMeta, entityMeta, TONE_TEXT, fieldLabel, renderValue, refLabel, diffFields } from '../../../components/patterns/auditDisplay.js';

export const PROJECT_PATH = (projectId: string, collection: string, id: string): string =>
  `/p/${projectId}/${collection}/${encodeURIComponent(id)}`;

export function actorName(ev: AuditEvent, t: TFunction): string {
  return ev.actor_name ?? ev.actor_id ?? t('actorSystem', { defaultValue: 'System' });
}
export function verb(ev: AuditEvent, t: TFunction): string {
  return t(`verb.${ev.action}`, { defaultValue: ev.action.replace(/_/g, ' ') });
}
// Actions whose verb already names the object, so a noun would read redundantly:
// identity/session events ("signed in", not "signed in user") + the solution-config
// + email verbs ("corrected the configuration", not "…configuration solution config")
// + the suggestion verbs ("ignored suggestions on Plant 4" — the site is WHERE, not
// what: the sentence joins the place with its own word, the verb stays a verb).
const NOUNLESS_ACTIONS = new Set<string>([
  'login', 'logout', 'login_fail', 'mfa_fail', 'password_reset', 'password_changed',
  'mfa_enrolled', 'mfa_self_enrolled', 'mfa_self_disabled', 'mfa_authenticator_reset', 'toggle_mfa',
  'config_changed', 'config_corrected', 'config_start_date_corrected', 'config_period_deleted',
  'suggestion_ignored', 'suggestion_restored',
  'email_sent',
]);
// Actions that happened TO the subject with nobody acting (the platform locked an
// account after failed sign-ins): the sentence leads with the subject — "bob was
// locked", never "System was locked user bob".
const SUBJECT_FIRST_ACTIONS = new Set<string>(['account_locked']);
// Actions whose subject is the PLACE the action happened on — the sentence joins it
// with the language's own word ("on" · "sur" · "على"), never the verb's tail.
const PLACE_ACTIONS = new Set<string>(['suggestion_ignored', 'suggestion_restored']);
export function subjectFirst(ev: AuditEvent): boolean {
  return SUBJECT_FIRST_ACTIONS.has(ev.action) && Boolean(ev.entity_label);
}
export function noun(ev: AuditEvent, t: TFunction): string {
  if (ev.entity_type === 'auth') return ''; // legacy rows
  if (NOUNLESS_ACTIONS.has(ev.action)) return '';
  return t(`entity.${ev.entity_type}`, { defaultValue: ev.entity_type.replace(/_/g, ' ') });
}
export function headline(ev: AuditEvent, t: TFunction): string {
  if (subjectFirst(ev)) return `${ev.entity_label} ${verb(ev, t)}`;
  const n = noun(ev, t);
  const base = `${actorName(ev, t)} ${verb(ev, t)}${n ? ` ${n}` : ''}`;
  if (!ev.entity_label) return base;
  const joiner = PLACE_ACTIONS.has(ev.action) ? ` ${t('joiner.on', { defaultValue: 'on' })}` : '';
  return `${base}${joiner} ${ev.entity_label}`;
}

// ── kind-aware leaf detail (ADR-058) — the leaf reads as one agreeable sentence;
// all forensics live in the peek. ─────────────────────────────────────────────
// A `link` edge → its target's snapshot label ({id,label}).
function edgeNames(edges: unknown): string[] {
  if (!Array.isArray(edges)) return [];
  return edges
    .map((e) => refLabel((e as { target?: unknown } | null)?.target))
    .filter((x): x is string => Boolean(x));
}

// An inline suffix appended to the sentence — the reason on a state change, the
// salient descriptor on an event ("wrong password", a record count, "delivered").
export function inlineDetail(ev: AuditEvent, t: TFunction): string | null {
  const d = (ev.new_value ?? null) as Record<string, unknown> | null;
  if (!d) return null;
  if (ev.kind === 'state_change') return typeof d.reason === 'string' ? d.reason : null;
  if (ev.kind === 'event') {
    if (typeof d.reason === 'string') return d.reason.replace(/_/g, ' ');
    if (typeof d.outcome === 'string') return t(`value.outcome.${d.outcome}`, { defaultValue: String(d.outcome) });
    if (typeof d.count === 'number') return t('detail.exportSummary', { count: d.count, format: String(d.format ?? '').toUpperCase() });
    if (typeof d.create === 'number') return t('detail.importSummary', { create: d.create, update: typeof d.update === 'number' ? d.update : 0 });
    if (typeof d.stage === 'string') return d.stage.replace(/_/g, ' ');
    if (typeof d.filename === 'string') return String(d.filename);
    if (typeof d.method === 'string') return String(d.method);
    return null;
  }
  return null;
}

// The relationship change below a `link` sentence: + added / − removed names.
export function LinkChips({ ev }: { ev: AuditEvent }) {
  const nv = (ev.new_value ?? null) as Record<string, unknown> | null;
  const added = edgeNames(nv?.added);
  const removed = edgeNames(nv?.removed);
  if (added.length === 0 && removed.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-col gap-1 text-xs">
      {added.length > 0 && (
        <span className="text-[var(--color-success)]">
          <bdi>+ {added.join(', ')}</bdi>
        </span>
      )}
      {removed.length > 0 && (
        <span className="text-[var(--color-danger)] line-through">
          <bdi>− {removed.join(', ')}</bdi>
        </span>
      )}
    </div>
  );
}

// Below-sentence detail shown in the LEAF: edited → before→after diff; link →
// added/removed edges; other kinds carry no leaf detail (peek holds their payload).
export function LeafDetail({ ev, t }: { ev: AuditEvent; t: TFunction }) {
  if (ev.kind === 'edited') return <DiffBlob ev={ev} t={t} />;
  if (ev.kind === 'link') return <LinkChips ev={ev} />;
  return null;
}

// A blob rendered as a labelled key→value list (peek): created/deleted snapshots,
// a state_change's to/params, an event descriptor. Excludes link edges (shown above).
export function PayloadList({ ev, blob, t }: { ev: AuditEvent; blob: Record<string, unknown> | null; t: TFunction }) {
  if (!blob) return null;
  const keys = Object.keys(blob).filter((k) => k !== 'added' && k !== 'removed');
  if (keys.length === 0) return null;
  return (
    <dl className="flex flex-col gap-1.5 text-xs">
      {keys.map((k) => (
        <div key={k} className="flex justify-between gap-4">
          <dt className="text-[var(--color-text-secondary)]">{fieldLabel(k, t)}</dt>
          <dd className="text-end text-[var(--color-text-primary)]">
            <bdi>{renderValue(ev.entity_type, k, blob[k], t)}</bdi>
          </dd>
        </div>
      ))}
    </dl>
  );
}

// The full payload block in the PEEK, per kind.
export function PeekDetail({ ev, t }: { ev: AuditEvent; t: TFunction }) {
  if (ev.kind === 'edited') return <DiffBlob ev={ev} t={t} />;
  if (ev.kind === 'link') {
    return (
      <div className="flex flex-col gap-3">
        <LinkChips ev={ev} />
        <PayloadList ev={ev} blob={(ev.new_value ?? null) as Record<string, unknown> | null} t={t} />
      </div>
    );
  }
  if (ev.kind === 'deleted') return <PayloadList ev={ev} blob={(ev.previous_value ?? null) as Record<string, unknown> | null} t={t} />;
  return <PayloadList ev={ev} blob={(ev.new_value ?? null) as Record<string, unknown> | null} t={t} />;
}

// The day header, in the screen's zone (the person's — ADR-082 §2): the day, "Today" / "Yesterday" judged on that
// zone's CALENDAR days against the read's own clock (`now` — the page's, so every caption agrees with the rows;
// yesterday is the calendar day before, never 24 hours back — a daylight day is 23 or 25 hours long).
export function dayLabel(iso: string, t: TFunction, zone: string, now: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const long = formatDate(iso, 'dateLong', { timeZone: zone }) ?? '';
  const day = dayIn(zone, d);
  const today = dayIn(zone, now);
  if (day === today) return `${t('timeline.today', { defaultValue: 'Today' })} · ${long}`;
  if (day === addDays(today, -1)) return `${t('timeline.yesterday', { defaultValue: 'Yesterday' })} · ${long}`;
  return long;
}

// ── glyph ─────────────────────────────────────────────────────────────────────
export function AuditGlyph({ ev }: { ev: AuditEvent }) {
  const { icon: Glyph, tone } = actionMeta(ev.action);
  return (
    <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--color-border-strong)] bg-[var(--color-bg-surface)]">
      <Glyph size={16} className={TONE_TEXT[tone]} aria-hidden />
    </span>
  );
}

// ── diff chips ──────────────────────────────────────────────────────────────
// A set-delta field value ({added?, removed?}) inside an `edited` diff — e.g. a
// role's permission / managed-role change; elements are codes or {id,label} refs.
function setDelta(entityType: string, field: string, v: unknown, t: TFunction): { added: string[]; removed: string[] } | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  if (!('added' in o) && !('removed' in o)) return null;
  const names = (arr: unknown): string[] => (Array.isArray(arr) ? arr.map((x) => renderValue(entityType, field, x, t)) : []);
  return { added: names(o.added), removed: names(o.removed) };
}

export function DiffBlob({ ev, t }: { ev: AuditEvent; t: TFunction }) {
  const fields = diffFields(ev);
  if (fields.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {fields.map((f) => {
        const delta = setDelta(ev.entity_type, f.field, f.next, t);
        if (delta) {
          return (
            <span key={f.field} className="inline-flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-[var(--color-text-secondary)]">{fieldLabel(f.field, t)}</span>
              {delta.added.length > 0 && (
                <span className="rounded-md bg-[var(--color-success)]/12 px-2 py-0.5 text-xs text-[var(--color-success)]">
                  <bdi>+ {delta.added.join(', ')}</bdi>
                </span>
              )}
              {delta.removed.length > 0 && (
                <span className="rounded-md bg-[var(--color-danger)]/10 px-2 py-0.5 text-xs text-[var(--color-danger)] line-through">
                  <bdi>− {delta.removed.join(', ')}</bdi>
                </span>
              )}
            </span>
          );
        }
        const hasPrev = f.prev !== undefined;
        const hasNext = f.next !== undefined;
        return (
          <span key={f.field} className="inline-flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-[var(--color-text-secondary)]">{fieldLabel(f.field, t)}</span>
            {hasPrev && (
              <span className="rounded-md bg-[var(--color-danger)]/10 px-2 py-0.5 text-xs text-[var(--color-danger)] line-through">
                <bdi>{renderValue(ev.entity_type, f.field, f.prev, t)}</bdi>
              </span>
            )}
            {hasPrev && hasNext && (
              <span className="inline-block text-xs text-[var(--color-text-secondary)] rtl:-scale-x-100" aria-hidden>
                →
              </span>
            )}
            {hasNext && (
              <span
                className={`rounded-md px-2 py-0.5 text-xs ${hasPrev ? 'bg-[var(--color-success)]/12 text-[var(--color-success)]' : 'bg-[var(--color-bg-surface-alt)] text-[var(--color-text-secondary)]'}`}
              >
                <bdi>{renderValue(ev.entity_type, f.field, f.next, t)}</bdi>
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

// ── entity name: a peek ref where one exists, else plain text ─────────────────
export function EntityName({ ev, projectId }: { ev: AuditEvent; projectId: string }) {
  const { openPeek } = useEntityPeek();
  const label = ev.entity_label;
  if (!label) return null;
  // Self-action (e.g. a sign-in) — the actor already names the subject; don't repeat it.
  if (ev.entity_id && ev.entity_id === ev.actor_id) return null;
  const meta = entityMeta(ev.entity_type);
  if (meta.peek && ev.entity_id) {
    const ref = `${meta.peek}:${ev.entity_id}`;
    return (
      <EntityRef
        name={label}
        to={PROJECT_PATH(projectId, meta.peek, ev.entity_id)}
        onPeek={() => openPeek(ref)}
        className="relative font-medium"
      />
    );
  }
  // No peek for this entity type → plain (non-interactive) emphasis.
  return <span className="font-medium text-[var(--color-text-primary)]">{label}</span>;
}

// ── actor: a user peek-ref when the actor is a resolvable person, else plain ──
export function ActorRef({ ev, projectId, t }: { ev: AuditEvent; projectId: string; t: TFunction }) {
  const { openPeek } = useEntityPeek();
  const name = actorName(ev, t);
  if (ev.actor_kind === 'person' && ev.actor_id) {
    return (
      <EntityRef
        quiet
        name={name}
        to={PROJECT_PATH(projectId, 'users', ev.actor_id)}
        onPeek={() => openPeek(`users:${ev.actor_id}`)}
        className="relative"
      />
    );
  }
  return <span className="font-medium">{name}</span>;
}

// ── row body ──────────────────────────────────────────────────────────────────
export function AuditRow({ ev, projectId, t }: { ev: AuditEvent; projectId: string; t: TFunction }) {
  const inline = inlineDetail(ev, t);
  const leadsWithSubject = subjectFirst(ev);
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3.5 py-3">
      <div className="text-[13px] leading-relaxed text-[var(--color-text-primary)]">
        {leadsWithSubject ? (
          <>
            <EntityName ev={ev} projectId={projectId} /> <span className="text-[var(--color-text-secondary)]">{verb(ev, t)}</span>
          </>
        ) : (
          <>
            <ActorRef ev={ev} projectId={projectId} t={t} />{' '}
            <span className="text-[var(--color-text-secondary)]">
              {verb(ev, t)}
              {noun(ev, t) ? ` ${noun(ev, t)}` : ''}
            </span>{' '}
            <EntityName ev={ev} projectId={projectId} />
          </>
        )}
        {inline && (
          <span className="text-[var(--color-text-secondary)]">
            {' · '}
            <bdi>{inline}</bdi>
          </span>
        )}
        {ev.channel === 'import' && (
          <span className="relative ms-1.5 inline-flex items-center gap-1 rounded bg-[var(--color-bg-surface-alt)] px-1.5 py-0.5 text-[11px] text-[var(--color-text-secondary)]">
            <icons.upload size={11} aria-hidden /> {t('peek.via', { defaultValue: 'via import' })}
          </span>
        )}
      </div>
      <LeafDetail ev={ev} t={t} />
    </div>
  );
}
